/**
 * Application Configuration
 *
 * Centralized configuration for the application.
 * Contains environment-aware settings and external URLs.
 */

// ============================================
// Environment Detection
// ============================================

export const APP_ENV = import.meta.env.VITE_APP_ENV || 'development';
export const IS_DEVELOPMENT = APP_ENV === 'development';
export const IS_PRODUCTION = APP_ENV === 'production';

// ============================================
// API Configuration
// ============================================

export const API_CONFIG = {
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000, // 30 seconds
};

// ============================================
// reCAPTCHA Configuration
// ============================================

export const RECAPTCHA_CONFIG = {
  siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LdV29MqAAAAAJJP-j4JJJAgkynFyH2msOjc15vL',
  action: 'login',
};

// ============================================
// External URLs Configuration
// ============================================

export const EXTERNAL_URLS = {
  /**
   * Support session URL for OTP verification redirect
   * This URL is used to open the admin login with session ID
   * Can be overridden via VITE_SUPPORT_SESSION_URL environment variable
   */
  supportSession: {
    development: import.meta.env.VITE_SUPPORT_SESSION_URL || 'https://cpaydev.icorei.com/adminLogin',
    production: import.meta.env.VITE_SUPPORT_SESSION_URL || 'https://cpay.icorei.com/adminLogin',
  },
};

/**
 * Host-specific support session URLs.
 *
 * Resolved at runtime from the hostname the app is served from, so a single
 * production build can point at different support portals per deployment.
 * Keys are hostnames only - no protocol, no port, no trailing slash.
 */
const SUPPORT_SESSION_URL_BY_HOST: Record<string, string> = {
  // Testing server: ships a production build, but support-login lives on cpaydev2.
  'cpaydev-admin.icorei.com': 'https://cpaydev2.icorei.com/support-login',
};

/**
 * Get the support session base URL for the current deployment.
 *
 * A host-specific entry wins over the environment default; otherwise falls back
 * to the VITE_APP_ENV-based choice.
 */
export function getSupportSessionBaseUrl(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const hostOverride = SUPPORT_SESSION_URL_BY_HOST[host];
  if (hostOverride) {
    return hostOverride;
  }

  return IS_PRODUCTION
    ? EXTERNAL_URLS.supportSession.production
    : EXTERNAL_URLS.supportSession.development;
}

/**
 * Get the support session URL based on current environment
 */
export function getSupportSessionUrl(sessionId: string): string {
  return `${getSupportSessionBaseUrl()}?sessionId=${sessionId}`;
}

// ============================================
// Pagination Configuration
// ============================================

export const PAGINATION_CONFIG = {
  /** Default page size */
  defaultPageSize: 10,
  /** Available page size options */
  pageSizeOptions: [5, 10, 20, 50],
};

// ============================================
// UI Configuration
// ============================================

export const UI_CONFIG = {
  /** Debounce delay for search inputs (ms) */
  searchDebounceMs: 500,
  /** Toast notification duration (ms) */
  toastDuration: 5000,
  /** Sidebar width when expanded */
  sidebarExpandedWidth: 280,
  /** Sidebar width when collapsed */
  sidebarCollapsedWidth: 80,
};

// ============================================
// Date Configuration
// ============================================

export const DATE_CONFIG = {
  /** Standard date format used throughout the app */
  dateFormat: 'dd-MM-yyyy',
  /** Standard datetime format used throughout the app */
  dateTimeFormat: 'dd-MM-yyyy HH:mm:ss',
  /** Time format */
  timeFormat: 'HH:mm:ss',
};

// ============================================
// App Info
// ============================================

export const APP_INFO = {
  name: 'CPRoll Admin Dashboard',
  version: '2.0.0',
  description: 'Admin portal for managing organizations, users, and subscriptions',
};
